/** Ce mois / plage vs période précédente — € HT et %. */
import { useEffect, useMemo, useState } from "react";
import { facturxFetch } from "../../api/client";
import { FxDate } from "../../components/FxDate";
import { useFormatters } from "../../formats/useFormatters";
import { CompareStrip } from "./CompareStrip";
import { demoEncaissementHt } from "./demo";
import {
  deltaPct,
  formatDeltaEuro,
  formatDeltaEuroMoney,
  formatDeltaPct,
  formatEuroHt,
  formatEuroHtMoney,
  formatMonthRange,
  formatRange,
  monthEndIso,
  monthStartIso,
  previousEqualSpan,
  previousMonthToDate,
  todayIso,
  type DateRange,
} from "./period";
import type { DashPayload } from "./types";

type Mode = "month" | "custom";

type EncaissementCompareProps = {
  demo: boolean;
  companyId: number;
};

export function EncaissementCompare({ demo, companyId }: EncaissementCompareProps) {
  const { money } = useFormatters();
  const [mode, setMode] = useState<Mode>("month");
  const [from, setFrom] = useState(monthStartIso);
  const [to, setTo] = useState(todayIso);
  const [live, setLive] = useState<{ current: number | null; previous: number | null }>({
    current: null,
    previous: null,
  });

  const rawFrom = mode === "month" ? monthStartIso() : from || monthStartIso();
  const rawTo = mode === "month" ? monthEndIso() : to || todayIso();
  const current: DateRange = rawFrom <= rawTo ? { from: rawFrom, to: rawTo } : { from: rawTo, to: rawFrom };
  const previous = mode === "month" ? previousMonthToDate(current) : previousEqualSpan(current);

  useEffect(() => {
    if (demo) {
      setLive({ current: null, previous: null });
      return;
    }
    const ac = new AbortController();
    facturxFetch<DashPayload>(
      `dashboard.php?company_id=${companyId}&from=${current.from}&to=${current.to}&mode=${mode}`,
      { signal: ac.signal },
    )
      .then((data) => {
        setLive({
          current: data.collected?.current_ht_cents ?? 0,
          previous: data.collected?.previous_ht_cents ?? 0,
        });
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setLive({ current: null, previous: null });
      });
    return () => ac.abort();
  }, [demo, companyId, current.from, current.to, mode]);

  const figures = useMemo(() => {
    if (demo) {
      const cur = demoEncaissementHt(current.from, current.to, false);
      const prev = demoEncaissementHt(previous.from, previous.to, true);
      return { current: cur, previous: prev, delta: cur - prev, pct: deltaPct(cur, prev) };
    }
    const cur = live.current;
    const prev = live.previous;
    if (cur === null || prev === null) {
      return { current: null, previous: null, delta: null, pct: null };
    }
    return { current: cur, previous: prev, delta: cur - prev, pct: deltaPct(cur, prev) };
  }, [demo, current.from, current.to, previous.from, previous.to, live.current, live.previous]);

  const showHt = demo
    ? formatEuroHt
    : (n: number | null) => formatEuroHtMoney(n, money);
  const showDelta = demo
    ? formatDeltaEuro
    : (n: number | null) => formatDeltaEuroMoney(n, money);

  const tone =
    figures.delta === null ? "" : figures.delta > 0 ? " is-up" : figures.delta < 0 ? " is-down" : "";

  return (
    <section className="fx-encaiss">
      <div className="fx-panel__head">
        <h2>Encaissé</h2>
        <div className="fx-encaiss__modes" role="group" aria-label="Période">
          <button type="button" className={mode === "month" ? "is-on" : ""} onClick={() => setMode("month")}>
            Ce mois
          </button>
          <button type="button" className={mode === "custom" ? "is-on" : ""} onClick={() => setMode("custom")}>
            Dates
          </button>
        </div>
      </div>
      {mode === "custom" ? (
        <div className="fx-encaiss__dates">
          <label className="fx-encaiss__date">
            <span>Du</span>
            <FxDate value={from} onChange={setFrom} />
          </label>
          <label className="fx-encaiss__date">
            <span>Au</span>
            <FxDate value={to || todayIso()} max={todayIso()} onChange={setTo} />
          </label>
        </div>
      ) : null}
      <div className="fx-dash-3">
        <article className="fx-tile">
          <p className="fx-encaiss__k">Cette période</p>
          <p className="fx-encaiss__v">{showHt(figures.current)}</p>
          <p className="fx-encaiss__sub">{mode === "month" ? formatMonthRange(current) : formatRange(current)}</p>
        </article>
        <article className="fx-tile">
          <p className="fx-encaiss__k">Période d’avant</p>
          <p className="fx-encaiss__v">{showHt(figures.previous)}</p>
          <p className="fx-encaiss__sub">{mode === "month" ? formatMonthRange(previous) : formatRange(previous)}</p>
        </article>
        <article className={`fx-tile fx-encaiss__delta${tone}`}>
          <p className="fx-encaiss__k">Écart</p>
          <p className="fx-encaiss__v">{showDelta(figures.delta)}</p>
          <p className="fx-encaiss__pct">{formatDeltaPct(figures.pct)}</p>
        </article>
      </div>
      <CompareStrip current={figures.current} previous={figures.previous} format={showHt} />
    </section>
  );
}
